/**
 * Calendar tab (WO-22) — an agenda grouped by day for the upcoming window.
 *
 * Loads listScheduleEvents({ from: start-of-today, to: +N days }) and groups the
 * results by local day. Each event row shows: a type icon + label, the time (or
 * "All day"), a FIXED vs FLEXIBLE indicator (is_flexible), and a priority badge.
 * A range selector toggles the window (7 / 30 / 90 days). "+ New event" reveals
 * an inline create form. Tapping an event pushes /(app)/schedule/[id].
 *
 * Empty/loading/error states never crash. NEVER imports @smartbox/*. No raw styles.
 */

import { useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { Stack, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listScheduleEvents,
  createScheduleEvent,
} from '../../src/api/calendar';
import {
  Screen,
  Text,
  Card,
  Button,
  Badge,
  ListRow,
  Select,
  priorityBadge,
} from '../../src/components/ui';
import { ScheduleEventForm } from '../../src/components/schedule/ScheduleEventForm';
import { scheduleEventMeta } from '../../src/lib/scheduleMeta';
import {
  formatDayLabel,
  formatTime,
  dayKey,
  startOfTodayIso,
  isoDaysFromNow,
  extractErrorMessage,
} from '../../src/lib/format';
import { theme } from '../../src/theme';
import type { ScheduleEventDto, CreateScheduleEventRequest } from '@beam/api-types';

const RANGE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '7', label: 'Next 7 days' },
  { value: '30', label: 'Next 30 days' },
  { value: '90', label: 'Next 90 days' },
];

type DayGroup = { key: string; iso: string; events: ScheduleEventDto[] };

function groupByDay(events: ScheduleEventDto[]): DayGroup[] {
  const map = new Map<string, DayGroup>();
  const undated: ScheduleEventDto[] = [];
  for (const ev of events) {
    if (!ev.starts_at) {
      undated.push(ev);
      continue;
    }
    const k = dayKey(ev.starts_at);
    let g = map.get(k);
    if (!g) {
      g = { key: k, iso: ev.starts_at, events: [] };
      map.set(k, g);
    }
    g.events.push(ev);
  }
  const groups = Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  for (const g of groups) {
    g.events.sort((a, b) => (a.starts_at ?? '').localeCompare(b.starts_at ?? ''));
  }
  if (undated.length) {
    groups.push({ key: 'undated', iso: '', events: undated });
  }
  return groups;
}

function EventRow({ event, onPress }: { event: ScheduleEventDto; onPress: () => void }) {
  const meta = scheduleEventMeta(event.event_type);
  const pri = priorityBadge(event.priority);
  const timeText = event.all_day
    ? 'All day'
    : event.starts_at
      ? formatTime(event.starts_at)
      : 'Unscheduled';
  const flexText = event.is_flexible ? 'Flexible' : 'Fixed';
  return (
    <ListRow
      title={event.title}
      subtitle={`${meta.label} · ${timeText} · ${flexText}`}
      left={<Ionicons name={meta.icon as never} size={22} color={theme.color.action.primary} />}
      right={<Badge status={pri.key} label={pri.label} />}
      onPress={onPress}
    />
  );
}

export default function CalendarScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [rangeDays, setRangeDays] = useState('30');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const from = startOfTodayIso();
  const to = isoDaysFromNow(parseInt(rangeDays, 10) + 1);

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: ['schedule-events', { from, to }],
    queryFn: () => listScheduleEvents({ from, to }),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateScheduleEventRequest) => createScheduleEvent(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['schedule-events'] });
      setCreating(false);
      setCreateError(null);
    },
    onError: (err) => setCreateError(extractErrorMessage(err, 'Could not create event.')),
  });

  const groups = useMemo(() => groupByDay(data ?? []), [data]);

  return (
    <>
      <Stack.Screen options={{ title: 'Calendar' }} />
      <Screen scroll>
      <Select
        label="Window"
        options={RANGE_OPTIONS}
        value={rangeDays}
        onChange={setRangeDays}
      />

      {creating ? (
        <ScheduleEventForm
          busy={createMutation.isPending}
          submitLabel="Create event"
          onCancel={() => {
            setCreating(false);
            setCreateError(null);
          }}
          onSubmit={(v) => createMutation.mutate(v)}
        />
      ) : (
        <Button
          title="+ New event"
          onPress={() => setCreating(true)}
          accessibilityLabel="Create a new schedule event"
        />
      )}

      {createError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {createError}
          </Text>
        </Card>
      ) : null}

      {isLoading ? (
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading calendar"
          />
        </View>
      ) : isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(error, 'Failed to load events.')}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => refetch()}
          />
        </Card>
      ) : groups.length === 0 ? (
        <View style={styles.centeredState}>
          <Text variant="title">Nothing scheduled</Text>
          <Text variant="body" color="secondary" style={styles.centeredText}>
            No events in this window. Tap “+ New event” to add one.
          </Text>
        </View>
      ) : (
        groups.map((g) => (
          <View key={g.key} style={styles.dayGroup}>
            <Text variant="label" color="secondary">
              {g.key === 'undated' ? 'Unscheduled' : formatDayLabel(g.iso)}
            </Text>
            <View style={styles.dayEvents}>
              {g.events.map((ev) => (
                <EventRow
                  key={ev.id}
                  event={ev}
                  onPress={() => router.push(`/(app)/schedule/${ev.id}`)}
                />
              ))}
            </View>
          </View>
        ))
      )}

      {isRefetching ? (
        <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Refreshing" />
      ) : null}
      </Screen>
    </>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingVertical: theme.spacing['3xl'],
    paddingHorizontal: theme.spacing['2xl'],
  },
  centeredText: { textAlign: 'center' },
  dayGroup: { gap: theme.spacing.sm },
  dayEvents: { gap: theme.spacing.sm },
});
